import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
    BasketComment,
    BasketItem,
    BasketItemRequest,
    DeleteAttachmentFileRequest,
    SupplierBasket,
    CompanySetting,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';
import { CatalogType } from '@lib/catalog/product-tree-filter/ui';
import { TextModuleCategoryEnum } from '@lib/shared/text-module';
import { DeliveryCondition, DeliveryConditionsComponent } from '@lib/delivery-conditions';
import { PaymentTerm } from '@lib/payment-terms';
import { FrameContractSelection } from '@lib/frame-contract-selection';
import {
    BudgetAssignmentType,
    IBudgetCostObject,
    IBudgetExceedance,
    IPurchasingGroupResolution,
} from '@lib/shared/basket-service';

@Component({
    selector: 'basket-supplier-ui',
    templateUrl: './basket-supplier-ui.component.html',
    styleUrl: './basket-supplier-ui.component.scss',
    standalone: false,
})
export class BasketSupplierUiComponent implements OnChanges {
    @ViewChild(DeliveryConditionsComponent) public selectionComponent!: DeliveryConditionsComponent;

    @Input() supplierBasket: SupplierBasket | null;
    @Input() basketItems: BasketItem[] | null;
    @Input() isEditableStep!: boolean;
    @Input() companySetting!: CompanySetting;
    @Input() companyCodeId: string | null = null;

    @Input() basketToggleStatus = false;
    @Input() deliveryCondition?: DeliveryCondition | null;
    @Input() paymentTerms?: PaymentTerm[] | null;
    @Input() isPaymentTermRequired = false;

    @Input() frameContracts?: FrameContractSelection[] | null;
    @Input() generalAgreementRequired = false;
    @Input() budgetOverruns: IBudgetExceedance[] | null = [];
    @Input() resolvedPurchasingGroups: readonly IPurchasingGroupResolution[] = [];
    @Input() purchasingGroupVisible = true;

    @Output() basketItemDeleted = new EventEmitter<BasketItemRequest>();
    @Output() attachmentOpened = new EventEmitter<string>();
    @Output() supplierCommentSaved = new EventEmitter<BasketComment>();
    @Output() attachmentFileDeleted = new EventEmitter<DeleteAttachmentFileRequest>();
    @Output() personCommentSaved = new EventEmitter<BasketComment>();

    @Output() productNumberValidationMessageShown = new EventEmitter<boolean>();
    @Output() desiredDeliveryDateValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();

    @Output() accountingValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() classificationValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() materialGroupValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();
    @Output() siteValidated = new EventEmitter<{ valid: boolean; basketItemId: number }>();

    @Output() deliveryConditionCreated = new EventEmitter<DeliveryCondition>();
    @Output() deliveryConditionUpdated = new EventEmitter<DeliveryCondition>();
    @Output() deliveryConditionDeleted = new EventEmitter<void>();
    @Output() paymentTermSelectionOpened: EventEmitter<void> = new EventEmitter<void>();
    @Output() paymentTermDeleted = new EventEmitter<void>();
    @Output() paymentTermValidated = new EventEmitter<boolean>();

    @Output() frameContractSelectionChanged = new EventEmitter<number | null>();
    @Output() frameContractValidated = new EventEmitter<boolean>();

    catalogType = CatalogType;

    textModuleCategoryEnum = TextModuleCategoryEnum;

    supplierId!: string;

    getCurrentConditionCheck = false;

    showDeliveryCondition = true;

    showCommentForApproverTextarea = false;

    showCommentForSupplierTextarea = false;

    budgetAssignmentType = BudgetAssignmentType;
    budgetWarningsByItem = new Map<string, IBudgetExceedance[]>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['supplierBasket']) {
            if (this.supplierBasket?.commentForApprover) {
                this.showCommentForApproverTextarea = true;
            }

            if (this.supplierBasket?.commentForSupplier) {
                this.showCommentForSupplierTextarea = true;
            }
        }
        if (changes['supplierBasket'] && this.selectionComponent && !this.supplierBasket) {
            this.showDeliveryCondition = false;
        } else {
            this.showDeliveryCondition = true;
        }

        if (
            changes['supplierBasket'] &&
            this.supplierBasket &&
            this.supplierBasket?.supplier &&
            !this.getCurrentConditionCheck
        ) {
            this.supplierId = this.supplierBasket?.supplier.id;
            this.getCurrentConditionCheck = true;
        }

        if (changes['deliveryCondition'] && this.deliveryCondition) {
            this.selectionComponent.disableEditing();
        } else if (this.selectionComponent) {
            this.selectionComponent.enableEditing();
        }

        if (changes['budgetOverruns']) {
            this.rebuildBudgetWarningMap();
        }
    }

    public create(newCondition: DeliveryCondition): void {
        this.deliveryConditionCreated.emit(newCondition);
    }

    public update(newCondition: DeliveryCondition): void {
        this.deliveryConditionUpdated.emit(newCondition);
    }

    public deleteCurrent(): void {
        this.deliveryConditionDeleted.emit();
    }

    deleteBasketItem(basketItemRequest: BasketItemRequest) {
        this.basketItemDeleted.emit(basketItemRequest);
    }

    onShowProductNumberValidationMessage(isShowMessage: boolean) {
        this.productNumberValidationMessageShown.emit(isShowMessage);
    }

    onDesiredDeliveryDateValidated(event: { valid: boolean; basketItemId: number }) {
        this.desiredDeliveryDateValidated.emit(event);
    }

    onAccountingValidated(event: { valid: boolean; basketItemId: number }) {
        this.accountingValidated.emit(event);
    }

    onSiteValidated(event: { valid: boolean; basketItemId: number }) {
        this.siteValidated.emit(event);
    }

    onClassificationValidated(event: { valid: boolean; basketItemId: number }) {
        this.classificationValidated.emit(event);
    }

    onMaterialGroupValidated(event: { valid: boolean; basketItemId: number }) {
        this.materialGroupValidated.emit(event);
    }

    isFreightCostOnlyItemForEverySupplier(supplierBasket: SupplierBasket): boolean {
        const items = supplierBasket.basketItems;

        const hasFreightCostItem = items.some((item) => item.product.type === this.catalogType.FREIGHT_COST);
        const isOnlyOneItem = items.length === 1;

        if (!hasFreightCostItem || !isOnlyOneItem) {
            return false;
        }

        return true;
    }

    openAttachmentDialog(supplierBasketId: string) {
        this.attachmentOpened.emit(supplierBasketId);
    }

    saveSupplierComment(comment: string, supplierBasketId: string) {
        this.supplierCommentSaved.emit({
            comment,
            supplierBasketId: supplierBasketId,
        } as BasketComment);
    }

    deleteBasketAttachmentFile(attachmentId: string, supplierBasketId: string) {
        this.attachmentFileDeleted.emit({
            attachmentId,
            supplierBasketId,
        });
    }

    savePersonComment(comment: string, supplierBasketId: string) {
        this.personCommentSaved.emit({
            comment,
            supplierBasketId: supplierBasketId,
        } as BasketComment);
    }

    onPaymentTermSelectionOpen() {
        this.paymentTermSelectionOpened.emit();
    }

    deleteCurrentPaymentTerm() {
        this.paymentTermDeleted.emit();
    }

    onPaymentTermValidated(isValid: boolean) {
        this.paymentTermValidated.emit(isValid);
    }

    onFrameContractSelectionChanged(frameContractId: number | null) {
        this.frameContractSelectionChanged.emit(frameContractId);
    }

    onFrameContractValidated(valid: boolean) {
        this.frameContractValidated.emit(valid);
    }

    toggleShowCommentForApproverTextarea() {
        this.showCommentForApproverTextarea = !this.showCommentForApproverTextarea;
    }

    toggleCommentForSupplierTextarea() {
        this.showCommentForSupplierTextarea = !this.showCommentForSupplierTextarea;
    }

    getCostObject(type: BudgetAssignmentType, costObjects?: readonly IBudgetCostObject[]) {
        return costObjects?.find((costObject) => costObject.type === type);
    }

    private rebuildBudgetWarningMap(): void {
        const warningMap = new Map<string, IBudgetExceedance[]>();
        (this.budgetOverruns ?? []).forEach((overrun) => {
            (overrun.items ?? []).forEach((item) => {
                if (!item || !item.basketItemId) {
                    return;
                }
                const itemKey = item.basketItemId;
                const existingWarnings = warningMap.get(itemKey) ?? [];
                warningMap.set(itemKey, [...existingWarnings, overrun]);
            });
        });
        this.budgetWarningsByItem = warningMap;
    }
}
